/**
 * Favicon 图标管理工具
 * 用于动态设置和更新网站图标
 */

import { API_ENDPOINTS } from '@/config/index'

/**
 * 获取图标的完整URL
 * @param {string} iconUrl - 图标路径
 * @returns {string} 完整的图标URL
 */
export const getFaviconUrl = (iconUrl) => {
  if (!iconUrl) return ''
  
  // 如果已经是完整的URL（包含协议），直接返回
  if (iconUrl.startsWith('http://') || iconUrl.startsWith('https://') || iconUrl.startsWith('data:')) {
    return iconUrl
  }
  
  // 如果是相对路径，转换为绝对路径
  const baseUrl = API_ENDPOINTS.GENERAL
  return baseUrl + iconUrl
}

/**
 * 更新网站图标
 * @param {string} iconUrl - 图标URL或路径
 */
export const updateFavicon = (iconUrl) => {
  let link = document.querySelector("link[rel*='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    link.type = 'image/x-icon'
    document.head.appendChild(link)
  }
  
  const faviconUrl = getFaviconUrl(iconUrl)
  link.href = faviconUrl
  
  // 同时设置shortcut icon（兼容性）
  let shortcutLink = document.querySelector("link[rel='shortcut icon']")
  if (!shortcutLink) {
    shortcutLink = document.createElement('link')
    shortcutLink.rel = 'shortcut icon'
    shortcutLink.type = 'image/x-icon'
    document.head.appendChild(shortcutLink)
  }
  shortcutLink.href = faviconUrl
}

/**
 * 从API获取站点设置并更新favicon
 * @returns {Promise<void>}
 */
export const initFaviconFromSettings = async () => {
  try {
    const response = await fetch(`${API_ENDPOINTS.GENERAL}/api/settings/site`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    })
    
    if (response.ok) {
      const result = await response.json()
      if ((result.code === 0 || result.code === 200) && result.data && result.data.favicon) {
        updateFavicon(result.data.favicon)
        
        // 同时更新页面标题
        if (result.data.siteName) {
          document.title = result.data.siteName
        }
      }
    }
  } catch (error) {
    console.warn('获取站点设置失败，使用默认favicon:', error.message)
    // 如果API失败，尝试使用数据库中的默认值
    updateFavicon('/upload/favicon/favicon_1754488478.ico')
  }
}

/**
 * 设置默认favicon（从数据库配置）
 */
export const setDefaultFavicon = () => {
  // 使用数据库中配置的favicon路径
  updateFavicon('/upload/favicon/favicon_1754488478.ico')
}

/**
 * 监听favicon变化的工具函数
 * @param {Function} callback - 当favicon变化时的回调函数
 * @returns {Function} 取消监听的函数
 */
export const watchFavicon = (callback) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'href') {
        const target = mutation.target
        if (target.rel && target.rel.includes('icon')) {
          callback(target.href)
        }
      }
    })
  })
  
  const faviconLinks = document.querySelectorAll("link[rel*='icon']")
  faviconLinks.forEach(link => {
    observer.observe(link, { attributes: true, attributeFilter: ['href'] })
  })
  
  return () => observer.disconnect()
}